package org.teddapp.project.ui.finance

import org.teddapp.project.ui.TeddSize

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Finance_category
import kotlinx.coroutines.launch
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

private val CATEGORY_COLORS = listOf(
    "#22508A", "#2D7A4F", "#A3612A", "#B4433A", "#6F7D8C", "#0F7C8C", "#B8892C", "#9A6B3A",
)
private val CATEGORY_ICONS = listOf("wallet", "cards", "home", "book", "heart", "spark", "flag", "target", "list", "grid")

/**
 * Gestion des catégories de dépense et de revenu : créer, renommer, changer l'icône et la
 * couleur, supprimer. Les catégories servies aux transactions et aux listes viennent d'ici.
 */
@Composable
internal fun FinanceCategoryManager(
    categories: List<Finance_category>,
    repos: TeddRepositories,
    onClose: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var type by remember { mutableStateOf("expense") }
    var editing by remember { mutableStateOf<Finance_category?>(null) }
    var creating by remember { mutableStateOf(false) }
    val visible = categories.filter { it.is_deleted == 0L && it.type == type }.sortedBy { it.order_index }

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(520.dp)
                .heightIn(max = 640.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    Text("Catégories", style = TeddType.Title2, color = TeddTokens.Ink)
                    Text("Elles servent aux transactions et aux listes de dépenses.", color = TeddTokens.InkMuted, fontSize = 12.sp)
                }
                TeddIconButton(onClick = onClose, size = 30.dp) {
                    Box(modifier = Modifier.padding(2.dp)) {
                        Text("×", color = TeddTokens.InkMuted, fontSize = 20.sp)
                    }
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(TeddTokens.RSm)).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)).padding(3.dp),
            ) {
                listOf("expense" to "Dépenses", "income" to "Revenus").forEach { (key, label) ->
                    val on = type == key
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .height(32.dp)
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .then(if (on) Modifier.background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm)) else Modifier)
                            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { type = key },
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(label, color = if (on) TeddTokens.Ink else TeddTokens.InkMuted, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
                    }
                }
            }

            Column(
                modifier = Modifier.weight(1f).verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                if (visible.isEmpty()) {
                    Text("Aucune catégorie de ce type.", color = TeddTokens.InkMuted, fontSize = 13.sp, modifier = Modifier.padding(vertical = 12.dp))
                }
                visible.forEach { cat ->
                    val color = runCatching { parseFinHex(cat.color) }.getOrElse { TeddTokens.LimeStrong }
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                            .padding(horizontal = 12.dp, vertical = 10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        Box(
                            modifier = Modifier.size(28.dp).clip(RoundedCornerShape(TeddTokens.RSm)).background(color.copy(alpha = 0.14f), RoundedCornerShape(TeddTokens.RSm)),
                            contentAlignment = Alignment.Center,
                        ) { TeddIcon(name = cat.icon, size = 13.dp, color = color, strokeWidth = 2.dp) }
                        Text(cat.name, color = TeddTokens.Ink, fontSize = 13.5.sp, fontWeight = FontWeight.Medium, modifier = Modifier.weight(1f))
                        TeddIconButton(onClick = { editing = cat }, size = 28.dp) {
                            TeddIcon(name = "edit", size = 13.dp, color = TeddTokens.InkMuted)
                        }
                        TeddIconButton(onClick = { scope.launch { repos.financeCategories.softDelete(cat.id) } }, size = 28.dp) {
                            TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted)
                        }
                    }
                }
            }

            TeddButton(
                text = "Nouvelle catégorie",
                onClick = { creating = true },
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Md,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = { TeddIcon(name = "plus", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
            )
        }
    }

    if (creating || editing != null) {
        CategoryForm(
            existing = editing,
            defaultType = type,
            onClose = { creating = false; editing = null },
            onSave = { name, catType, icon, color ->
                val target = editing
                scope.launch {
                    if (target == null) {
                        repos.financeCategories.create(
                            name = name, type = catType, icon = icon, color = color,
                            orderIndex = categories.count { it.type == catType }.toLong(),
                        )
                    } else {
                        repos.financeCategories.update(
                            id = target.id, name = name, type = catType, icon = icon, color = color, orderIndex = target.order_index,
                        )
                    }
                }
                creating = false
                editing = null
            },
        )
    }
}

@Composable
private fun CategoryForm(
    existing: Finance_category?,
    defaultType: String,
    onClose: () -> Unit,
    onSave: (name: String, type: String, icon: String, color: String) -> Unit,
) {
    var name by remember(existing) { mutableStateOf(existing?.name ?: "") }
    var type by remember(existing) { mutableStateOf(existing?.type ?: defaultType) }
    var icon by remember(existing) { mutableStateOf(existing?.icon ?: CATEGORY_ICONS.first()) }
    var color by remember(existing) { mutableStateOf(existing?.color ?: CATEGORY_COLORS.first()) }

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(TeddSize.SidePanel)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text(if (existing == null) "Nouvelle catégorie" else "Modifier la catégorie", style = TeddType.Title2, color = TeddTokens.Ink)
            Column {
                Text("NOM", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                Box(
                    modifier = Modifier.fillMaxWidth().height(44.dp).clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)).padding(horizontal = 12.dp),
                    contentAlignment = Alignment.CenterStart,
                ) {
                    BasicTextField(
                        value = name,
                        onValueChange = { name = it },
                        singleLine = true,
                        textStyle = TeddType.Body.copy(color = TeddTokens.Ink, fontSize = 14.sp),
                        cursorBrush = SolidColor(TeddTokens.LimeStrong),
                        modifier = Modifier.fillMaxWidth(),
                        decorationBox = { inner ->
                            if (name.isEmpty()) Text("Loyer, transport, salaire…", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                            inner()
                        },
                    )
                }
            }
            Column {
                Text("TYPE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    listOf("expense" to "Dépense", "income" to "Revenu").forEach { (key, label) ->
                        val on = type == key
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(TeddTokens.RFull))
                                .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { type = key }
                                .padding(horizontal = 12.dp, vertical = 6.dp),
                        ) {
                            Text(label, color = if (on) TeddTokens.OnAction else TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
            Column {
                Text("COULEUR", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    CATEGORY_COLORS.forEach { hex ->
                        val c = runCatching { parseFinHex(hex) }.getOrElse { TeddTokens.LimeStrong }
                        Box(
                            modifier = Modifier.size(28.dp).clip(RoundedCornerShape(TeddTokens.RSm)).background(c, RoundedCornerShape(TeddTokens.RSm))
                                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { color = hex },
                            contentAlignment = Alignment.Center,
                        ) { if (color == hex) TeddIcon(name = "check", size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.6.dp) }
                    }
                }
            }
            Column {
                Text("ICÔNE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    CATEGORY_ICONS.forEach { n ->
                        val on = icon == n
                        Box(
                            modifier = Modifier.size(28.dp).clip(RoundedCornerShape(TeddTokens.RSm))
                                .background(if (on) TeddTokens.Ink else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { icon = n },
                            contentAlignment = Alignment.Center,
                        ) { TeddIcon(name = n, size = 13.dp, color = if (on) TeddTokens.Bg else TeddTokens.InkMuted, strokeWidth = 2.dp) }
                    }
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
                TeddButton(
                    text = if (existing == null) "Créer" else "Enregistrer",
                    onClick = { onSave(name.trim(), type, icon, color) },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    enabled = name.isNotBlank(),
                )
            }
        }
    }
}
